/** Shared browser transport. A failed request must never resemble valid empty data. */
export class ApiFailure extends Error {
  readonly status: number;
  readonly field: string | null;
  constructor(status: number, message: string, field: string | null = null) {
    super(message);
    this.name = "ApiFailure";
    this.status = status;
    this.field = field;
  }
}

const unavailable = "We could not reach the service. Please try again shortly.";

export async function requestJson<T>(url: string, method: string, body?: unknown, headers: Record<string, string> = {}): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method, credentials: "same-origin",
      headers: { ...headers, accept: "application/json", ...(body === undefined ? {} : { "content-type": "application/json" }) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiFailure(0, unavailable);
  }
  if (res.status >= 500) throw new ApiFailure(res.status, unavailable);
  if (res.status === 204) return null as T;
  if (!/^application\/(?:[\w.+-]+\+)?json(?:;|$)/i.test(res.headers.get("content-type") ?? "")) {
    throw new ApiFailure(res.ok ? 502 : res.status, unavailable);
  }
  let data: unknown;
  try { data = await res.json(); } catch { throw new ApiFailure(502, unavailable); }
  if (!res.ok) {
    const err = data !== null && typeof data === "object" ? data as Record<string, unknown> : {};
    const detail = typeof err.detail === "string" ? err.detail : "The request could not be completed. Please check your details and try again.";
    throw new ApiFailure(res.status, detail, typeof err.field === "string" ? err.field : null);
  }
  return data as T;
}
